import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { shape, string, func, bool } from 'prop-types';
import { Heading, Loader } from '@tesla/design-system-react';
import styles from './styles.module.css.js';
import { PAGES, PERSONAL_INFO_URL, ACCOUNTS_URL_PROD, ACCOUNTS_URL_STAGE } from './pages.js';
import getTranslation from '../translation.js';
import { formatLocale, getShopLocale, getNavLink } from '../../../utils/getNavLink/index.js';
import { pushGAInteraction } from '../../../utils/googleAnalytics/index.js';

var Chevron = function Chevron(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("g", {
    transform: "matrix(1 0 0 -1 .271 5.59)",
    fill: "#393C41",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("rect", {
    transform: "scale(1 -1) rotate(-45 -1.307 0)",
    x: "5.186",
    width: "1.5",
    height: "6",
    rx: ".75"
  }), /*#__PURE__*/React.createElement("rect", {
    transform: "rotate(-45 2.75 3)",
    x: "2",
    width: "1.5",
    height: "6",
    rx: ".75"
  })));
};
Chevron.defaultProps = {
  "'aria-label'": "hidden",
  width: "9",
  height: "5",
  xmlns: "http://www.w3.org/2000/svg"
};
var Close = function Close(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    d: "M2.052.352 8 6.3 13.948.352a1.202 1.202 0 0 1 1.7 1.7L9.7 8l5.948 5.948a1.202 1.202 0 0 1-1.7 1.7L8 9.7l-5.948 5.948a1.202 1.202 0 0 1-1.7-1.7L6.3 8 .352 2.052a1.202 1.202 0 1 1 1.7-1.7Z",
    fill: "#171A20",
    fillRule: "evenodd"
  }));
};
Close.defaultProps = {
  "'aria-label'": "hidden",
  width: "16",
  height: "16",
  xmlns: "http://www.w3.org/2000/svg"
};
var Navigation = function Navigation(_ref) {
  var currentPage = _ref.currentPage,
    isChargingEnabled = _ref.isChargingEnabled,
    isCNEnv = _ref.isCNEnv,
    isContactView = _ref.isContactView,
    isLootBoxEnabled = _ref.isLootBoxEnabled,
    isOpen = _ref.isOpen,
    isPaymentMethodEnabled = _ref.isPaymentMethodEnabled,
    isProdEnv = _ref.isProdEnv,
    locale = _ref.locale,
    setIsOpen = _ref.setIsOpen;
  var formattedLocale = formatLocale(locale);
  var _getTranslation = getTranslation(formattedLocale),
    translations = _getTranslation["default"];
  var isShopEnabled = getShopLocale(formattedLocale) !== null;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showLoader = _useState2[0],
    setShowLoader = _useState2[1];
  var accountsBaseUrl = isProdEnv ? ACCOUNTS_URL_PROD : ACCOUNTS_URL_STAGE;
  var navList = Object.values(PAGES).map(function (targetPage) {
    var NAME = targetPage.NAME,
      BASE_ROUTE = targetPage.BASE_ROUTE,
      ICON = targetPage.ICON;
    var targetLink = getNavLink(currentPage, targetPage, formattedLocale);
    var GA_INTERACTION = {
      event: 'account',
      interaction: NAME,
      label: 'menu'
    };
    if (isContactView) {
      if ([PAGES.PAYMENT_METHOD.NAME].includes(NAME) || [PAGES.CHARGING_HISTORY.NAME].includes(NAME) || [PAGES.REFER_AND_EARN.NAME].includes(NAME) || [PAGES.ORDER_HISTORY.NAME].includes(NAME)) {
        return '';
      }
    }
    if (!isShopEnabled && [PAGES.ORDER_HISTORY.NAME].includes(NAME)) {
      return '';
    }
    if (!isChargingEnabled && [PAGES.CHARGING_HISTORY.NAME].includes(NAME)) {
      return '';
    }
    if (!isPaymentMethodEnabled && [PAGES.PAYMENT_METHOD.NAME].includes(NAME)) {
      return '';
    }
    if (!isLootBoxEnabled && [PAGES.REFER_AND_EARN.NAME].includes(NAME)) {
      return '';
    }
    var click = function click() {
      if (currentPage.NAME === NAME) {
        setIsOpen(!isOpen);
      } else {
        if (PAGES.SIGN_OUT.NAME === NAME) {
          setShowLoader(true);
        }
        pushGAInteraction(GA_INTERACTION);
      }
    };
    var navItemContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: styles.Scrim
    }, /*#__PURE__*/React.createElement(ICON, null)), translations[NAME]);
    var redirectOnClick = function redirectOnClick() {
      pushGAInteraction({
        event: 'account',
        interaction: 'profile-settings-redirect',
        label: 'menu'
      });
    };
    return /*#__PURE__*/React.createElement("li", {
      className: "".concat(styles.NavigationLink, " ").concat(currentPage.NAME === NAME ? styles.Selected : ''),
      key: NAME
    }, !isCNEnv && NAME === PAGES.PROFILE_SETTINGS.NAME ? /*#__PURE__*/React.createElement("a", {
      href: "".concat(accountsBaseUrl).concat(PERSONAL_INFO_URL.replace('##LOCALE##', locale)),
      target: "_blank",
      rel: "noreferrer noopener",
      onClick: redirectOnClick
    }, navItemContent) : /*#__PURE__*/React.createElement(React.Fragment, null, currentPage.BASE_ROUTE === BASE_ROUTE ? /*#__PURE__*/React.createElement(Link, {
      to: targetLink,
      onClick: click
    }, navItemContent) : /*#__PURE__*/React.createElement("a", {
      href: targetLink,
      onClick: click
    }, navItemContent)));
  });
  var NAME = currentPage.NAME,
    ICON = currentPage.ICON;
  var navHeading = /*#__PURE__*/React.createElement(Heading, {
    is: "h1",
    looksLike: "h3",
    className: styles.NavigationTitle
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.Scrim
  }, /*#__PURE__*/React.createElement(ICON, null)), translations[NAME], /*#__PURE__*/React.createElement("div", {
    className: styles.Chevron
  }, /*#__PURE__*/React.createElement(Chevron, null)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Loader, {
    show: showLoader
  }), isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Close, {
    onClick: function onClick() {
      return setIsOpen(!isOpen);
    },
    className: styles.CloseButton
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.UiBlocker
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: function onClick() {
      return setIsOpen(!isOpen);
    },
    className: "".concat(styles.NavigationDropdown, " ").concat(isOpen ? styles.IsOpen : '')
  }, navHeading), /*#__PURE__*/React.createElement("ul", {
    className: "tds-list ".concat(styles.Navigation, " ").concat(isOpen ? styles.IsOpen : '')
  }, navList));
};
Navigation.propTypes = {
  currentPage: shape({
    ROUTE: string,
    NAME: string,
    APP: string
  }).isRequired,
  setIsOpen: func.isRequired,
  locale: string,
  isChargingEnabled: bool,
  isCNEnv: bool,
  isContactView: bool,
  isLootBoxEnabled: bool,
  isOpen: bool.isRequired,
  isPaymentMethodEnabled: bool,
  isProdEnv: bool
};
Navigation.defaultProps = {
  isChargingEnabled: false,
  isCNEnv: false,
  isContactView: false,
  isLootBoxEnabled: false,
  isPaymentMethodEnabled: true,
  isProdEnv: true,
  locale: 'en_US'
};

export { Navigation as default };
